/* eslint-disable no-unused-vars */
import { Button, makeStyles, TextField, Typography, Accordion as MuiAccordion, AccordionSummary as MuiAccordionSummary, AccordionDetails, withStyles } from '@material-ui/core';
import _ from 'lodash';
import { LoadingButtonNoGrid } from 'components/basic/LoadingButton';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { useAlerts } from 'hooks/alerts-hooks';
import ToggleButton from '@material-ui/lab/ToggleButton';
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axiosClient from 'http/axios-client';
import { useQuery } from 'react-query';
import { LoaderSkeleton } from 'components/basic/LoaderSkeleton';
import { useDebounce } from 'hooks/util-hooks';

function capitalizeFirstLetter(string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

const Accordion = withStyles({
  root: {
    border: '1px solid rgba(0, 0, 0, .125)',
    // boxShadow: 'none',
    // boxShadow: 'rgba(0, 0, 0, 0.16) 0px 10px 36px 0px, rgba(0, 0, 0, 0.06) 0px 0px 0px 1px',
    boxShadow: 'rgba(17, 12, 46, 0.15) 0px 48px 100px 0px',
    '&:not(:last-child)': {
      borderBottom: 0,
    },
    '&:before': {
      display: 'none',
    },
  },
  expanded: {},
})(MuiAccordion);

const AccordionSummary = withStyles({
  root: {
    backgroundColor: 'rgba(0, 0, 0, .03)',
    borderBottom: '1px solid rgba(0, 0, 0, .125)',
    marginBottom: -1,
    minHeight: 56,
    '&$expanded': {
      minHeight: 56,
    },
  },
  content: {
    '&$expanded': {
      margin: '12px 0',
    },
  },
  expanded: {},
})(MuiAccordionSummary);

const ItemAccordion = ({ confItem: [ name, value ], debouncedSearch, itemKey, setLangConfigToSave }) => {
  const isValueString = useMemo(() => {
    return 'string' === typeof value || value instanceof String;
  }, [ ]);

  const updateText = (e) => {
    setLangConfigToSave((prev) => {
      const copy = _.cloneDeep(prev);

      const val = _.get(copy.value, itemKey);

      if (val || '' === val) {
        _.set(copy.value, itemKey, e.target.value);
      }

      return copy;
    });
  };

  return (
    <Accordion TransitionProps={ { unmountOnExit: true } }>
      {!isValueString && (
        <AccordionSummary
          expandIcon={ <ExpandMoreIcon /> }
          aria-controls='panel1a-content'
        >
          <Typography style={ { textTransform: 'capitalize' } }>{name.replaceAll('_', ' ')}</Typography>
        </AccordionSummary>
      )}

      <AccordionDetails style={ { display: 'flex', flexDirection: 'column' } }>
        {
          isValueString ? (
            <TextField
              multiline
              label={ capitalizeFirstLetter(name.replaceAll('_', ' ')) }
              variant='outlined'
              value={ isValueString ? value : '' }
              onChange={ updateText }
            />
          ) : (
            <>
              {
                Object.entries(value).map((s) => {
                  return (
                    <ItemAccordion
                      key={ s[0] }
                      confItem={ s }
                      debouncedSearch={ debouncedSearch }
                      itemKey={ `${ itemKey }.${ s[0] }` }
                      setLangConfigToSave={ setLangConfigToSave }
                    />
                  );
                })
              }
            </>
          )
        }
      </AccordionDetails>
    </Accordion>
  );
};

// strict object check
function isObject(value) {
  return '[object Object]' === toString.call(value);
}

// strict array check
function isArray(value) {
  return '[object Array]' === toString.call(value);
}

function propPicker(data = {}, value) {
  // type guard for data parameter
  // ensure it is an object
  if (!isObject(data)) {
    throw new TypeError('first parameter must be an object');
  }

  // the object or result to return
  let output = {};

  // iterate through data
  // to pick properties
  Object.keys(data).forEach((key) => {
    // add key (property) to the result to be
    // returned if it is present on the
    // data object
    if (('string' === typeof data[key] || data[key] instanceof String) && data?.[key].toLowerCase().trim()?.includes(value.toLowerCase().trim())) {
      output[key] = data[key];
    // eslint-disable-next-line brace-style
    }

    // recursive traverse the key (property)
    // if it is an object
    else if (isObject(data[key])) {
      const selectedProps = propPicker(data[key], value);
      let obj;

      // add keys (properties) from nested object
      // if the key is present on the data object
      if (Object.keys(selectedProps).length) {
        obj = {
          [key]: selectedProps,
        };
      }

      // merge main object and keys with
      // nested objects with filtered keys
      output = { ...output, ...obj };
    }
  });

  return output;
}

const TranslationsPage = () => {
  const [ language, setLanguage ] = useState('el');
  const [ updateLoading, setUpdateLoading ] = useState(false);
  const [ loading, setLoading ] = useState(false);

  const { alertSuccess } = useAlerts();

  const [ search, setSearch ] = useState('');

  const debouncedSearch = useDebounce(search, 200);

  const [ downloadLoading, setDownloadLoading ] = useState(false);

  const { data, isLoading } = useQuery([ 'translations' ], () => {
    return axiosClient.get('content-translations').then((s) => { return s; });
  });

  const [ enConfig, setEnConfig ] = useState(null);
  const [ elConfig, setElConfig ] = useState(null);
  const [ tempConfig, setTempConfig ] = useState(null);

  useEffect(() => {
    if (!data) { return; }

    setEnConfig(_.cloneDeep(data.find((s) => { return 'en-US' === s.language; })));
    setElConfig(_.cloneDeep(data.find((s) => { return 'el-GR' === s.language; })));
  }, [ data ]);

  useEffect(() => {
    if (null == elConfig || null == enConfig) { return; }

    setLoading(true);

    let trans;

    if ('el' === language) {
      trans = _.cloneDeep(elConfig.value);
    } else {
      trans = _.cloneDeep(enConfig.value);
    }

    if ('' === debouncedSearch) {
      setTempConfig(trans);
      setLoading(false);
      return;
    }

    const temp = propPicker(trans, debouncedSearch);

    setTempConfig(temp);

    setLoading(false);
  }, [ debouncedSearch, elConfig, enConfig, language ]);

  const uploadTranslation = () => {
    setUpdateLoading(true);

    const elId = data.find((s) => { return 'el-GR' === s.language; })?.id;
    const enId = data.find((s) => { return 'en-US' === s.language; })?.id;

    axiosClient.put(`content-translations/${ elId }`, { value: elConfig.value })
      .then(() => {
        axiosClient.put(`content-translations/${ enId }`, { value: enConfig.value });
      })
      .then(() => {
        alertSuccess('Configuration updated successfully!');
        setUpdateLoading(false);
      });
  };

  if (isLoading) {
    return <LoaderSkeleton />;
  }

  return (
    <div>
      <div
        style={ {
          position: 'sticky',
          paddingTop: '6px',
          zIndex: 3000,
          float: 'right',
          top: '80px',
        } }
      >
        <LoadingButtonNoGrid
          label='Save'
          loading={ updateLoading }
          onClick={ uploadTranslation }
          variant='contained'
          color='primary'
        />
      </div>

      <ToggleButtonGroup
        value={ language }
        style={ {
          position: 'sticky',
          zIndex: 3000,
          background: 'white',
          marginRight: '10px',
          float: 'right',
          marginTop: '2px',
          top: '80px',
        } }
        exclusive
        onChange={ (e, newValue) => {
          if (null !== newValue) { setLanguage(newValue); }
        } }
      >
        <ToggleButton value='el' aria-label='bold'>
          Greek
        </ToggleButton>
        <ToggleButton value='en' aria-label='italic'>
          English
        </ToggleButton>
      </ToggleButtonGroup>

      <TextField
        variant='outlined'
        style={ {
          position: 'sticky',
          zIndex: 3000,
          background: 'white',
          marginRight: '10px',
          float: 'right',
          top: '80px',
        } }
        value={ search }
        onChange={ (e) => { setSearch(e.target.value); } }
        label='Search'
      />

      <div style={ { display: 'flex', alignItems: 'center', height: 'auto', marginBottom: '60px', gap: '30px' } }>
        <Typography style={ { textAlign: 'center' } } variant='h5'>
          Translations
        </Typography>

        <LoadingButtonNoGrid
          label='Download'
          variant='contained'
          style={ { display: isLoading || null == elConfig || null == enConfig ? 'none' : '' } }
          color='primary'
          loading={ downloadLoading }
          download={ `${ 'el' === language ? 'Greek' : 'English' }-Translations.json` }
          href={ `data:text/json;charset=utf-8,${ encodeURIComponent(
            JSON.stringify('el' === language ? elConfig?.value : enConfig?.value, null, '\t'),
          ) }` }
        />
      </div>

      {
        loading || ((null == tempConfig || 0 === Object.keys(tempConfig).length) && '' === search) ? (
          <LoaderSkeleton />
        ) : (
          <>
            {
              (null == tempConfig || 0 === Object.keys(tempConfig).length) ? (
                <Typography>
                  No results found
                </Typography>
              ) : (
                <>
                  {

                    Object.entries(tempConfig).map((confItem) => {
                      return (
                        <ItemAccordion
                          itemKey={ confItem[0] }
                          key={ confItem[0] }
                          confItem={ confItem }
                          debouncedSearch={ debouncedSearch }
                          setLangConfigToSave={ 'el' === language ? setElConfig : setEnConfig }
                        />
                      );
                    })
                  }
                </>
              )
            }
          </>
        )
      }
    </div>
  );
};

export default TranslationsPage;
